import {Component} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {AutoScraperBackendService} from '../auto-scraper-backend.service';
import {FormControl, FormGroup} from '@angular/forms';
import {DirectHit, PageResult, SchemaMask, SelectorResult, StaticValues} from '../model';
import {catchError} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {chainOrder, escapeHtml, hasSchemaStep, withSchemaStep, withoutSchemaSteps} from '../extraction-steps';

type FieldKey =
  | 'oem_brand' | 'oem_number' | 'oem_gtin'
  | 'internal_name' | 'internal_number' | 'internal_desc' | 'internal_group'
  | 'price_amount' | 'price_price' | 'price_delivery_text' | 'price_currency_text' | 'price_vat_text' | 'condition' | 'price_status';

/** The schema checkbox (if any) of each field row. */
const FIELD_SCHEMA_KEY: Partial<Record<FieldKey, keyof SchemaMask>> = {
  oem_brand: 'oem_info_brand', oem_number: 'oem_info_number', oem_gtin: 'oem_info_gtin',
  internal_name: 'internal_info_name', internal_number: 'internal_info_number',
  internal_desc: 'internal_info_description', internal_group: 'internal_info_group',
  price_price: 'prices_price', price_currency_text: 'prices_currency', price_vat_text: 'prices_vat',
};

interface FieldAccessors {
  getDefault: (idx: number) => string;
  setTarget: (idx: number, value: string) => void;
  setSelectors: (selectors: SelectorResult[]) => void;
  getBaselineSelectors: () => SelectorResult[];
  setPageResult: (idx: number, value: string) => void;
}

@Component({
  selector: 'app-direct-hit',
  templateUrl: './direct-hit.component.html',
  styleUrls: ['./direct-hit.component.css'],
  standalone: false
})
export class DirectHitComponent {
  constructor(private http: HttpClient, private router: Router, public data: AutoScraperBackendService) {}

  /** Shorthand accessor for the direct_hits array on the current implementation. */
  private get dh(): DirectHit[] {
    return this.data.supplier_implementation.direct_hits;
  }

  /** Shorthand accessor for direct_hits_selectors on the current implementation. */
  private get sel() {
    return this.data.supplier_implementation.direct_hits_selectors;
  }

  /** Returns static_values, initializing it to an empty object if absent. */
  get sv(): StaticValues {
    const dhs = this.data.supplier_implementation.direct_hits_selectors;
    if (!dhs.static_values) {
      dhs.static_values = {
        oem_info_brand: null, oem_info_number: null, oem_info_gtin: null,
        internal_info_number: null, internal_info_name: null, internal_info_description: null,
        internal_info_group: null, prices_price: null, prices_delivery_text: null, prices_currency_text: null,
        prices_vat_text: null,
      };
    }
    return dhs.static_values;
  }

  // From main: options list for the decimal-separator dropdown in the template
  decimal_separator_options = ['.', ','];

  /** Returns an Authorization header built from the stored token. */
  private get authHeaders(): HttpHeaders {
    return new HttpHeaders({'auth': this.data.token});
  }

  schema_status: string[] = ['', ''];
  parsedPrices: [string, string] = ['', ''];
  parsedCurrencies: [string, string] = ['', ''];
  parsedVats: [string, string] = ['', ''];

  fieldStatus: Record<FieldKey, string> = {
    oem_brand: '', oem_number: '', oem_gtin: '',
    internal_name: '', internal_number: '', internal_desc: '', internal_group: '',
    price_amount: '', price_price: '', price_delivery_text: '', price_currency_text: '', price_vat_text: '',
    condition: '', price_status: '',
  };

  /** Per field: does its chain contain a schema step. Mirrors the step lists, which are the source of truth. */
  useSchema: SchemaMask = this.schemaMaskFromSteps();

  /** The step list behind each schema checkbox. */
  private schemaSteps(key: keyof SchemaMask): {get: () => SelectorResult[]; set: (steps: SelectorResult[]) => void} {
    const lists: Record<keyof SchemaMask, [() => SelectorResult[], (s: SelectorResult[]) => void]> = {
      oem_info_brand: [() => this.sel.oem_info.brand, s => { this.sel.oem_info.brand = s; }],
      oem_info_number: [() => this.sel.oem_info.number, s => { this.sel.oem_info.number = s; }],
      oem_info_gtin: [() => this.sel.oem_info.gtin, s => { this.sel.oem_info.gtin = s; }],
      internal_info_number: [() => this.sel.internal_info.number, s => { this.sel.internal_info.number = s; }],
      internal_info_name: [() => this.sel.internal_info.name, s => { this.sel.internal_info.name = s; }],
      internal_info_description: [() => this.sel.internal_info.description, s => { this.sel.internal_info.description = s; }],
      internal_info_group: [() => this.sel.internal_info.group, s => { this.sel.internal_info.group = s; }],
      prices_price: [() => this.sel.prices.price, s => { this.sel.prices.price = s; }],
      prices_currency: [() => this.sel.prices.currency_text, s => { this.sel.prices.currency_text = s; }],
      prices_vat: [() => this.sel.prices.vat_text, s => { this.sel.prices.vat_text = s; }],
    };
    const [get, set] = lists[key];
    return {get: () => get() ?? [], set};
  }

  private schemaMaskFromSteps(): SchemaMask {
    const keys: (keyof SchemaMask)[] = [
      'oem_info_brand', 'oem_info_number', 'oem_info_gtin', 'internal_info_number', 'internal_info_name',
      'internal_info_description', 'internal_info_group', 'prices_price', 'prices_currency', 'prices_vat',
    ];
    const mask = {} as SchemaMask;
    keys.forEach(key => { mask[key] = hasSchemaStep(this.schemaSteps(key).get()); });
    return mask;
  }

  // ─── Form groups ──────────────────────────────────────────────────────────────
  oem_brand = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.oem_info.brand),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.oem_info.brand),
  });
  oem_number = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.oem_info.number),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.oem_info.number),
  });
  oem_gtin = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.oem_info.gtin),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.oem_info.gtin),
  });
  internal_name = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.internal_info.name),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.internal_info.name),
  });
  internal_number = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.internal_info.number),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.internal_info.number),
  });
  internal_desc = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.internal_info.description),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.internal_info.description),
  });
  internal_group = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.internal_info.group),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.internal_info.group),
  });
  price_amount = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.prices.amount),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.prices.amount),
  });
  price_price = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.prices.price),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.prices.price),
  });
  price_delivery_text = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.prices.delivery_text),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.prices.delivery_text),
  });
  price_currency_text = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.prices.currency_text),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.prices.currency_text),
  });
  price_vat_text = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.prices.vat_text),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.prices.vat_text),
  });
  condition = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.condition),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.condition),
  });
  price_status = new FormGroup({
    input0: new FormControl(this.data.supplier_implementation.direct_hits[0].target_values.price_status),
    input1: new FormControl(this.data.supplier_implementation.direct_hits[1].target_values.price_status),
  });

  /** Fetches schema-derived field values for hit[idx] from /get_schema and applies them to both target_values and page_result. */
  get_schema_values(idx: number) {
    this.schema_status[idx] = 'Working on it.';
    this.http.post(
      environment.apiUrl + '/get_schema',
      {page_source: this.dh[idx].page_source},
      {headers: this.authHeaders},
    ).pipe(
      catchError(() => { this.schema_status[idx] = 'Error'; return EMPTY; })
    ).subscribe(res => {
      if (typeof res === 'string') { this.schema_status[idx] = res; return; }
      const schema = res as PageResult;
      const applySchema = (target: PageResult) => {
        Object.assign(target.oem_info, {brand: schema.oem_info.brand, number: schema.oem_info.number, gtin: schema.oem_info.gtin});
        Object.assign(target.internal_info, {
          number: schema.internal_info.number, name: schema.internal_info.name,
          description: schema.internal_info.description, group: schema.internal_info.group,
        });
        Object.assign(target.prices, {
          amount: schema.prices.amount, price: schema.prices.price,
          delivery_text: schema.prices.delivery_text, delivery: schema.prices.delivery,
          currency_text: schema.prices.currency_text ?? null,
          vat_included: schema.prices.vat_included ?? null,
        });
      };
      applySchema(this.dh[idx].target_values);
      applySchema(this.dh[idx].page_result);
      this.schema_status[idx] = 'Found schema.';
      this.fetchParsedCurrency(idx);
      this.fetchParsedVat(idx);
    });
  }

  // ─── Field submission ─────────────────────────────────────────────────────────

  /** Returns the saved-baseline selectors; falls back to the live selectors when no baseline snapshot exists yet. */
  private get baselineSel() {
    return this.data.baseline_direct_hits_selectors
      ?? this.data.supplier_implementation.direct_hits_selectors;
  }

  /** POSTs both page sources with their target values to /search_identifier_for_direct_hit. */
  private find_selectors(v0: string, v1: string): Observable<unknown> {
    return this.http.post(environment.apiUrl + '/search_identifier_for_direct_hit', {
      search_items: [
        {page_source: this.dh[0].page_source, target_value: v0},
        {page_source: this.dh[1].page_source, target_value: v1},
      ],
    }, {headers: this.authHeaders});
  }

  /** POSTs newly found and baseline selectors to /merge_selectors, which deduplicates and ranks them server-side. */
  private merge_selectors(newSelectors: SelectorResult[], existingSelectors: SelectorResult[]): Observable<unknown> {
    return this.http.post(environment.apiUrl + '/merge_selectors', {
      new: newSelectors,
      existing: existingSelectors,
    }, {headers: this.authHeaders});
  }

  /** Runs the given selector against the second direct-hit page source to produce its extracted value. */
  private extractForHit1(selector: SelectorResult): Observable<unknown> {
    return this.http.post(environment.apiUrl + '/extract_value', {
      page_source: this.dh[1].page_source,
      value_to_extract: selector.value_to_extract,
      selector: selector.selector,
    }, {headers: this.authHeaders});
  }

  /** Returns true only when both target values are non-empty. */
  are_values_ok(v0: string, v1: string): boolean {
    return Boolean(v0 && v1);
  }

  /**
   * Core submission flow for a single field: validates inputs, searches for selectors,
   * merges with the baseline snapshot, sorts results by timestamp (newest first), and
   * writes the top selector back to the accessor. Calls afterSuccess once the flow completes.
   */
  private submitField(
    form: FormGroup,
    statusKey: FieldKey,
    acc: FieldAccessors,
    afterSuccess?: () => void,
  ) {
    const v0 = (form.value.input0 as string) || acc.getDefault(0);
    const v1 = (form.value.input1 as string) || acc.getDefault(1);

    if (!this.are_values_ok(v0, v1)) {
      this.fieldStatus[statusKey] = 'Missing target value';
      return;
    }

    this.fieldStatus[statusKey] = 'Working on it';
    acc.setTarget(0, v0);
    acc.setTarget(1, v1);
    acc.setPageResult(0, '');

    this.find_selectors(v0, v1).pipe(
      catchError(() => { this.fieldStatus[statusKey] = 'Error'; return EMPTY; })
    ).subscribe(res => {
      if (typeof res === 'string') { this.fieldStatus[statusKey] = res; return; }
      const newSelectors = res as SelectorResult[];
      if (newSelectors.length === 0) {
        this.fieldStatus[statusKey] = 'No selector found';
        return;
      }
      this.merge_selectors(newSelectors, acc.getBaselineSelectors()).pipe(
        catchError(() => { this.fieldStatus[statusKey] = 'Error'; return EMPTY; })
      ).subscribe(merged => {
        let sorted = (merged as SelectorResult[]).sort((a, b) => {
          const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return tb - ta;
        });
        // a schema step added since the baseline snapshot must survive the merge with that baseline
        const schemaKey = FIELD_SCHEMA_KEY[statusKey];
        if (schemaKey && this.useSchema[schemaKey]) {
          sorted = withSchemaStep(sorted);
        }
        acc.setSelectors(sorted);
        const newest = sorted.find(s => !s.is_schema) ?? sorted[0];
        acc.setPageResult(0, newest.extracted_value ?? '');
        this.extractForHit1(newest).subscribe(v => acc.setPageResult(1, v as string));
        this.fieldStatus[statusKey] = 'Success';
        afterSuccess?.();
      });
    });
  }

  // ─── Field submit handlers ────────────────────────────────────────────────────

  /** Searches for OEM brand selectors and merges results into the baseline. */
  onSubmit_oem_brand() {
    this.submitField(this.oem_brand, 'oem_brand', {
      getDefault: idx => this.dh[idx].target_values.oem_info.brand,
      setTarget: (idx, v) => { this.dh[idx].target_values.oem_info.brand = v; },
      setSelectors: s => { this.sel.oem_info.brand = s; },
      getBaselineSelectors: () => this.baselineSel.oem_info.brand,
      setPageResult: (idx, v) => { this.dh[idx].page_result.oem_info.brand = v; },
    });
  }

  /** Searches for OEM number selectors and merges results into the baseline. */
  onSubmit_oem_number() {
    this.submitField(this.oem_number, 'oem_number', {
      getDefault: idx => this.dh[idx].target_values.oem_info.number,
      setTarget: (idx, v) => { this.dh[idx].target_values.oem_info.number = v; },
      setSelectors: s => { this.sel.oem_info.number = s; },
      getBaselineSelectors: () => this.baselineSel.oem_info.number,
      setPageResult: (idx, v) => { this.dh[idx].page_result.oem_info.number = v; },
    });
  }

  /** Searches for OEM GTIN selectors and merges results into the baseline. */
  onSubmit_oem_gtin() {
    this.submitField(this.oem_gtin, 'oem_gtin', {
      getDefault: idx => this.dh[idx].target_values.oem_info.gtin,
      setTarget: (idx, v) => { this.dh[idx].target_values.oem_info.gtin = v; },
      setSelectors: s => { this.sel.oem_info.gtin = s; },
      getBaselineSelectors: () => this.baselineSel.oem_info.gtin,
      setPageResult: (idx, v) => { this.dh[idx].page_result.oem_info.gtin = v; },
    });
  }

  /** Searches for internal name selectors and merges results into the baseline. */
  onSubmit_internal_name() {
    this.submitField(this.internal_name, 'internal_name', {
      getDefault: idx => this.dh[idx].target_values.internal_info.name,
      setTarget: (idx, v) => { this.dh[idx].target_values.internal_info.name = v; },
      setSelectors: s => { this.sel.internal_info.name = s; },
      getBaselineSelectors: () => this.baselineSel.internal_info.name,
      setPageResult: (idx, v) => { this.dh[idx].page_result.internal_info.name = v; },
    });
  }

  /** Searches for internal number selectors and merges results into the baseline. */
  onSubmit_internal_number() {
    this.submitField(this.internal_number, 'internal_number', {
      getDefault: idx => this.dh[idx].target_values.internal_info.number,
      setTarget: (idx, v) => { this.dh[idx].target_values.internal_info.number = v; },
      setSelectors: s => { this.sel.internal_info.number = s; },
      getBaselineSelectors: () => this.baselineSel.internal_info.number,
      setPageResult: (idx, v) => { this.dh[idx].page_result.internal_info.number = v; },
    });
  }

  /** Searches for internal description selectors and merges results into the baseline. */
  onSubmit_internal_desc() {
    this.submitField(this.internal_desc, 'internal_desc', {
      getDefault: idx => this.dh[idx].target_values.internal_info.description,
      setTarget: (idx, v) => { this.dh[idx].target_values.internal_info.description = v; },
      setSelectors: s => { this.sel.internal_info.description = s; },
      getBaselineSelectors: () => this.baselineSel.internal_info.description,
      setPageResult: (idx, v) => { this.dh[idx].page_result.internal_info.description = v; },
    });
  }

  /** Searches for internal group selectors and merges results into the baseline. */
  onSubmit_internal_group() {
    this.submitField(this.internal_group, 'internal_group', {
      getDefault: idx => this.dh[idx].target_values.internal_info.group,
      setTarget: (idx, v) => { this.dh[idx].target_values.internal_info.group = v; },
      setSelectors: s => { this.sel.internal_info.group = s; },
      getBaselineSelectors: () => this.baselineSel.internal_info.group,
      setPageResult: (idx, v) => { this.dh[idx].page_result.internal_info.group = v; },
    });
  }

  /** Searches for price amount selectors and merges results into the baseline. */
  onSubmit_price_amount() {
    this.submitField(this.price_amount, 'price_amount', {
      getDefault: idx => this.dh[idx].target_values.prices.amount,
      setTarget: (idx, v) => { this.dh[idx].target_values.prices.amount = v; },
      setSelectors: s => { this.sel.prices.amount = s; },
      getBaselineSelectors: () => this.baselineSel.prices.amount,
      setPageResult: (idx, v) => { this.dh[idx].page_result.prices.amount = v; },
    });
  }

  /** Searches for price selectors, merges into the baseline, then triggers parsed-price fetching for both hits. */
  onSubmit_price_price() {
    this.parsedPrices = ['', ''];
    this.submitField(this.price_price, 'price_price', {
      getDefault: idx => this.dh[idx].target_values.prices.price,
      setTarget: (idx, v) => { this.dh[idx].target_values.prices.price = v; },
      setSelectors: s => { this.sel.prices.price = s; },
      getBaselineSelectors: () => this.baselineSel.prices.price,
      setPageResult: (idx, v) => { this.dh[idx].page_result.prices.price = v; },
    }, () => { this.fetchParsedPrices(); this.fetchParsedCurrencies(); this.fetchParsedVats(); });
  }

  /** Requests locale-parsed decimal prices for both hits from /get_parsed_price; decimal_separator drives backend parsing. */
  private fetchParsedPrices() {
    [0, 1].forEach(idx => {
      this.http.post(environment.apiUrl + '/get_parsed_price', {
        raw_price: this.dh[idx].page_result.prices.price,
        language: this.data.supplier_implementation.basic_supplier_info.language,
        decimal_separator: this.data.supplier_implementation.basic_supplier_info.decimal_separator,
      }, {headers: this.authHeaders}).subscribe(res => {
        this.parsedPrices[idx] = ` [${res as string}]`;
      });
    });
  }

  /** Searches for delivery text selectors and merges results into the baseline. */
  onSubmit_price_delivery_text() {
    this.submitField(this.price_delivery_text, 'price_delivery_text', {
      getDefault: idx => this.dh[idx].target_values.prices.delivery_text,
      setTarget: (idx, v) => { this.dh[idx].target_values.prices.delivery_text = v; },
      setSelectors: s => { this.sel.prices.delivery_text = s; },
      getBaselineSelectors: () => this.baselineSel.prices.delivery_text,
      setPageResult: (idx, v) => { this.dh[idx].page_result.prices.delivery_text = v; },
    });
  }

  /** Searches for currency text selectors, merges into the baseline, then resolves the ISO code for both hits. */
  onSubmit_price_currency_text() {
    this.parsedCurrencies = ['', ''];
    this.submitField(this.price_currency_text, 'price_currency_text', {
      getDefault: idx => this.dh[idx].target_values.prices.currency_text ?? '',
      setTarget: (idx, v) => { this.dh[idx].target_values.prices.currency_text = v; },
      setSelectors: s => { this.sel.prices.currency_text = s; },
      getBaselineSelectors: () => this.baselineSel.prices.currency_text,
      setPageResult: (idx, v) => {
        this.dh[idx].page_result.prices.currency_text = v;
        if (idx === 1) this.fetchParsedCurrency(1);
      },
    }, () => this.fetchParsedCurrency(0));
  }

  /**
   * Text the backend parses the currency from: static value, extracted currency text, else the extracted price text
   * (inference falls back to the price text, since many shops render "US $12.99").
   */
  private currencySource(idx: number): string | null {
    return this.sv.prices_currency_text
      || this.dh[idx].page_result.prices.currency_text
      || this.dh[idx].page_result.prices.price
      || null;
  }

  /** Requests the ISO 4217 code for both hits from /get_parsed_currency. */
  fetchParsedCurrencies() {
    [0, 1].forEach(idx => this.fetchParsedCurrency(idx));
  }

  /** Requests the ISO 4217 code for hit[idx]; uses the supplier's currency_mapping and default currency like inference. */
  private fetchParsedCurrency(idx: number) {
    const info = this.data.supplier_implementation.basic_supplier_info;
    this.http.post<string | null>(environment.apiUrl + '/get_parsed_currency', {
      currency_text: this.currencySource(idx),
      currency_mapping: info.currency_mapping ?? {},
      default_currency: info.currency || null,
    }, {headers: this.authHeaders}).pipe(
      catchError(() => { this.parsedCurrencies[idx] = ' [error]'; return EMPTY; })
    ).subscribe(res => {
      this.parsedCurrencies[idx] = ` [${res ?? 'unknown'}]`;
    });
  }

  /** Searches for VAT text selectors, merges into the baseline, then resolves the VAT status for both hits. */
  onSubmit_price_vat_text() {
    this.parsedVats = ['', ''];
    this.submitField(this.price_vat_text, 'price_vat_text', {
      getDefault: idx => this.dh[idx].target_values.prices.vat_text ?? '',
      setTarget: (idx, v) => { this.dh[idx].target_values.prices.vat_text = v; },
      setSelectors: s => { this.sel.prices.vat_text = s; },
      getBaselineSelectors: () => this.baselineSel.prices.vat_text,
      setPageResult: (idx, v) => {
        this.dh[idx].page_result.prices.vat_text = v;
        if (idx === 1) this.fetchParsedVat(1);
      },
    }, () => this.fetchParsedVat(0));
  }

  /**
   * Text the backend parses the VAT status from: static value, extracted VAT text, else the extracted price text
   * (inference falls back to the price text, since many shops render "19,99 € inkl. MwSt.").
   */
  private vatSource(idx: number): string | null {
    return this.sv.prices_vat_text
      || this.dh[idx].page_result.prices.vat_text
      || this.dh[idx].page_result.prices.price
      || null;
  }

  /** Requests the VAT status for both hits from /get_parsed_vat. */
  fetchParsedVats() {
    [0, 1].forEach(idx => this.fetchParsedVat(idx));
  }

  /**
   * Resolves whether hit[idx]'s displayed price includes VAT, in inference order: static value, schema
   * (valueAddedTaxIncluded from /get_schema), then the page text via /get_parsed_vat with the supplier's vat_mapping.
   * The default is deliberately not sent, so a text that says nothing shows up as the supplier default.
   */
  private fetchParsedVat(idx: number) {
    const info = this.data.supplier_implementation.basic_supplier_info;
    const schemaVat = this.dh[idx].page_result.prices.vat_included;
    if (!this.sv.prices_vat_text && this.useSchema.prices_vat && typeof schemaVat === 'boolean') {
      this.parsedVats[idx] = ` [${this.vatLabel(schemaVat)} (schema)]`;
      return;
    }
    this.http.post<boolean | null>(environment.apiUrl + '/get_parsed_vat', {
      vat_text: this.vatSource(idx),
      vat_mapping: info.vat_mapping ?? {},
      default_vat_included: null,
    }, {headers: this.authHeaders}).pipe(
      catchError(() => { this.parsedVats[idx] = ' [error]'; return EMPTY; })
    ).subscribe(res => {
      this.parsedVats[idx] = typeof res === 'boolean'
        ? ` [${this.vatLabel(res)}]`
        : ` [supplier default: ${this.vatLabel(info.vat_included_on_displayed_price)}]`;
    });
  }

  private vatLabel(vatIncluded: boolean | null | undefined): string {
    if (vatIncluded === true) return 'incl. VAT';
    if (vatIncluded === false) return 'excl. VAT';
    return 'unknown';
  }

  /** Searches for condition selectors and merges results into the baseline. */
  onSubmit_condition() {
    this.submitField(this.condition, 'condition', {
      getDefault: idx => this.dh[idx].target_values.condition,
      setTarget: (idx, v) => { this.dh[idx].target_values.condition = v; },
      setSelectors: s => { this.sel.condition = s; },
      getBaselineSelectors: () => this.baselineSel.condition,
      setPageResult: (idx, v) => { this.dh[idx].page_result.condition = v; },
    });
  }

  /** Searches for price_status selectors and merges results into the baseline. */
  onSubmit_price_status() {
    this.submitField(this.price_status, 'price_status', {
      getDefault: idx => this.dh[idx].target_values.price_status,
      setTarget: (idx, v) => { this.dh[idx].target_values.price_status = v; },
      setSelectors: s => { this.sel.price_status = s; },
      getBaselineSelectors: () => this.baselineSel.price_status,
      setPageResult: (idx, v) => { this.dh[idx].page_result.price_status = v; },
    });
  }

  // ─── Static value blur handler ───────────────────────────────────────────────

  /**
   * Clears form inputs, target values and extracted values for a field when a static value is confirmed.
   * The field's steps are kept: the static value overrides them, and they apply again once it is removed.
   */
  onStaticValueBlur(svKey: keyof StaticValues) {
    if (!this.sv[svKey]) return;

    const configs: Partial<Record<keyof StaticValues, {
      form: FormGroup;
      setTarget: (i: number, v: string) => void;
      setResult: (i: number, v: string) => void;
      afterClear?: () => void;
    }>> = {
      oem_info_brand:           { form: this.oem_brand,           setTarget: (i, v) => { this.dh[i].target_values.oem_info.brand = v; },           setResult: (i, v) => { this.dh[i].page_result.oem_info.brand = v; } },
      oem_info_number:          { form: this.oem_number,          setTarget: (i, v) => { this.dh[i].target_values.oem_info.number = v; },          setResult: (i, v) => { this.dh[i].page_result.oem_info.number = v; } },
      oem_info_gtin:            { form: this.oem_gtin,            setTarget: (i, v) => { this.dh[i].target_values.oem_info.gtin = v; },            setResult: (i, v) => { this.dh[i].page_result.oem_info.gtin = v; } },
      internal_info_name:       { form: this.internal_name,       setTarget: (i, v) => { this.dh[i].target_values.internal_info.name = v; },       setResult: (i, v) => { this.dh[i].page_result.internal_info.name = v; } },
      internal_info_number:     { form: this.internal_number,     setTarget: (i, v) => { this.dh[i].target_values.internal_info.number = v; },     setResult: (i, v) => { this.dh[i].page_result.internal_info.number = v; } },
      internal_info_description:{ form: this.internal_desc,       setTarget: (i, v) => { this.dh[i].target_values.internal_info.description = v; },setResult: (i, v) => { this.dh[i].page_result.internal_info.description = v; } },
      internal_info_group:      { form: this.internal_group,      setTarget: (i, v) => { this.dh[i].target_values.internal_info.group = v; },      setResult: (i, v) => { this.dh[i].page_result.internal_info.group = v; } },
      prices_price:             { form: this.price_price,         setTarget: (i, v) => { this.dh[i].target_values.prices.price = v; },             setResult: (i, v) => { this.dh[i].page_result.prices.price = v; }, afterClear: () => { this.parsedPrices = ['', '']; } },
      prices_delivery_text:     { form: this.price_delivery_text, setTarget: (i, v) => { this.dh[i].target_values.prices.delivery_text = v; },    setResult: (i, v) => { this.dh[i].page_result.prices.delivery_text = v; } },
      prices_currency_text:     { form: this.price_currency_text, setTarget: (i, v) => { this.dh[i].target_values.prices.currency_text = v; },    setResult: (i, v) => { this.dh[i].page_result.prices.currency_text = v; }, afterClear: () => this.fetchParsedCurrencies() },
      prices_vat_text:          { form: this.price_vat_text,      setTarget: (i, v) => { this.dh[i].target_values.prices.vat_text = v; },         setResult: (i, v) => { this.dh[i].page_result.prices.vat_text = v; }, afterClear: () => this.fetchParsedVats() },
    };

    const cfg = configs[svKey];
    if (!cfg) return;
    [0, 1].forEach(i => { cfg.setTarget(i, ''); cfg.setResult(i, ''); });
    cfg.form.setValue({input0: '', input1: ''});
    cfg.afterClear?.();
  }

  // ─── Schema toggles ───────────────────────────────────────────────────────────

  /** Adds a schema step as first step to every field that supports schema; existing selectors stay as fall-through. */
  use_all_schema_values() {
    (Object.keys(this.useSchema) as (keyof SchemaMask)[]).forEach(key => {
      this.useSchema[key] = true;
      this.onChangeSchema(key);
    });
  }

  /**
   * Adds (checked) or removes (unchecked) the schema step of a field. A new schema step runs first; the
   * selectors stay as fall-through for pages without schema. Order is changed in the Selectors Manager.
   */
  onChangeSchema(key: keyof SchemaMask) {
    const steps = this.schemaSteps(key);
    steps.set(this.useSchema[key] ? withSchemaStep(steps.get()) : withoutSchemaSteps(steps.get()));
    if (key === 'prices_price') {
      this.parsedPrices = ['', ''];
    }
    if (key === 'prices_currency') {
      this.parsedCurrencies = ['', ''];
    }
    if (key === 'prices_vat') {
      this.parsedVats = ['', ''];
    }
  }

  // ─── Selectors overlay ────────────────────────────────────────────────────────

  currentOverlayAnchor = '';

  private readonly keyToAnchor: Record<string, string> = {
    oem_brand:           'sec-oem_info-brand',
    oem_number:          'sec-oem_info-number',
    oem_gtin:            'sec-oem_info-gtin',
    internal_name:       'sec-internal_info-name',
    internal_number:     'sec-internal_info-number',
    internal_desc:       'sec-internal_info-description',
    internal_group:      'sec-internal_info-group',
    price_amount:        'sec-prices-amount',
    price_price:         'sec-prices-price',
    price_delivery_text: 'sec-prices-delivery_text',
    price_currency_text: 'sec-prices-currency_text',
    price_vat_text:      'sec-prices-vat_text',
    condition:           'sec-condition',
    price_status:        'sec-price-status',
  };

  private readonly selectorMap: Record<string, () => SelectorResult[]> = {
    oem_brand:           () => this.sel.oem_info.brand,
    oem_number:          () => this.sel.oem_info.number,
    oem_gtin:            () => this.sel.oem_info.gtin,
    internal_name:       () => this.sel.internal_info.name,
    internal_number:     () => this.sel.internal_info.number,
    internal_desc:       () => this.sel.internal_info.description,
    internal_group:      () => this.sel.internal_info.group,
    price_price:         () => this.sel.prices.price,
    price_amount:        () => this.sel.prices.amount,
    price_delivery_text: () => this.sel.prices.delivery_text,
    price_currency_text: () => this.sel.prices.currency_text,
    price_vat_text:      () => this.sel.prices.vat_text,
    condition:           () => this.sel.condition,
    price_status:        () => this.sel.price_status,
  };

  /** Closes the overlay and navigates to the selector manager page, scrolling to the currently displayed field anchor. */
  navigateToManager(): void {
    this.overlay_off();
    this.router.navigate(['/direct_hit_selectors'], {fragment: this.currentOverlayAnchor});
  }

  /** Renders the top-5 selectors for the given field key into the overlay panel and makes it visible. */
  showSelectors(key: string) {
    const getList = this.selectorMap[key];
    if (!getList) return;
    this.currentOverlayAnchor = this.keyToAnchor[key] ?? '';
    const max = 5;
    const list = chainOrder(getList()).slice(0, max);
    const label = key.replace(/_/g, ' ');

    const titleEl = document.getElementById('overlay-title');
    if (titleEl) titleEl.innerText = `Selectors: ${label}`;

    let html = `<p class="sel-section-title">first ${max} steps (selectors and schema) for ${label}, in the order extraction tries them</p>
    <table class="sel-modal-table">
      <thead>
        <tr>
          <th style="width:2.5rem">#</th>
          <th style="width:30%">Extracted value</th>
          <th>Selector(s)</th>
          <th style="width:8rem">Timestamp</th>
        </tr>
      </thead>
      <tbody>`;

    for (let i = 0; i < list.length; i++) {
      const sel = list[i];
      const paths = sel.is_schema
        ? `<em>Schema</em> ${(sel.schema_paths ?? []).map(p => `<code>${escapeHtml(p.join('.'))}</code>`).join(', ') || '(default lookup)'}`
        : sel.selector.map(p => `<code>${p}</code>`).join('<br>');
      html += `<tr>
        <td>${i}</td>
        <td>${sel.extracted_value ?? ''}</td>
        <td>${paths}</td>
        <td>${sel.timestamp ?? ''}</td>
      </tr>`;
    }
    if (list.length === 0) {
      html += `<tr><td colspan="4" style="text-align:center;color:var(--bp-muted)">No selectors found</td></tr>`;
    }
    html += `</tbody></table>`;

    document.getElementById('text')!.innerHTML = html;
    document.getElementById('overlay')!.style.display = 'block';
  }

  /** Hides the selectors overlay panel. */
  overlay_off() {
    document.getElementById('overlay')!.style.display = 'none';
  }

  /** Re-fetches parsed prices for both hits when the decimal separator changes; no-op if no prices have been extracted yet. */
  onChangeDecimalSeparator() {
    const raw0 = this.dh[0].page_result.prices.price;
    const raw1 = this.dh[1].page_result.prices.price;

    this.parsedPrices = ['', ''];

    if (!raw0 && !raw1) {
      return;
    }

    [raw0, raw1].forEach((raw, idx) => {
      if (!raw) return;
      this.http.post<string>(environment.apiUrl + '/get_parsed_price', {
        raw_price: raw,
        language: this.data.supplier_implementation.basic_supplier_info.language,
        decimal_separator: this.data.supplier_implementation.basic_supplier_info.decimal_separator,
      }, {headers: this.authHeaders}).pipe(
        catchError(() => EMPTY)
      ).subscribe(res => {
        this.parsedPrices[idx] = ` [${res}]`;
      });
    });
  }
}
